/**
 * Admin Activity Page
 * User activity tracking with DAU/WAU/MAU metrics
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Flex,
  Input,
  Select,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useToast,
  Spinner,
  HStack,
  VStack,
  Avatar,
  Grid,
  GridItem,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from '@chakra-ui/react';
import { Search, RefreshCw, Users, Activity, TrendingUp } from 'lucide-react';
import {
  adminService,
  type UserActivity,
  type GetActivityParams,
  type ActivityStatsResponse,
  type UserActivityDetailResponse,
} from '../../services/AdminService';
import { formatDistanceToNow, format } from 'date-fns';

export const ActivityPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // State
  const [users, setUsers] = useState<UserActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<ActivityStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });

  // User detail modal
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userDetail, setUserDetail] = useState<UserActivityDetailResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'last_activity' | 'total_requests' | 'username'>('last_activity');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load activity stats
  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await adminService.getActivityStats();
      setStats(response);
    } catch (error: any) {
      toast({
        title: 'Error loading activity stats',
        description: error.response?.data?.detail || 'Failed to load stats',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setStatsLoading(false);
    }
  }, [toast]);

  // Load users activity
  const loadUsers = useCallback(async (params?: Partial<GetActivityParams>) => {
    try {
      setLoading(true);
      const response = await adminService.getUsersActivity({
        search: searchQuery || undefined,
        sort: sortBy,
        order: sortOrder,
        page: pagination.page,
        limit: pagination.limit,
        ...params,
      });

      setUsers(response.users);
      setPagination(response.pagination);
    } catch (error: any) {
      toast({
        title: 'Error loading users activity',
        description: error.response?.data?.detail || 'Failed to load users',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortBy, sortOrder, pagination.page, pagination.limit, toast]);

  // Load user detail
  const loadUserDetail = useCallback(async (userId: string) => {
    try {
      setDetailLoading(true);
      const response = await adminService.getUserActivityDetail(userId, 30);
      setUserDetail(response);
    } catch (error: any) {
      toast({
        title: 'Error loading user activity',
        description: error.response?.data?.detail || 'Failed to load activity',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDetailLoading(false);
    }
  }, [toast]);

  // Initial load
  useEffect(() => {
    loadStats();
    loadUsers();
  }, []);

  // Handle search
  const handleSearch = () => {
    loadUsers({ page: 1 });
  };

  // Handle sort change
  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const [sort, order] = e.target.value.split('-') as [typeof sortBy, typeof sortOrder];
    setSortBy(sort);
    setSortOrder(order);
    loadUsers({ page: 1, sort, order });
  };

  // Handle refresh
  const handleRefresh = () => {
    loadStats();
    loadUsers({ page: pagination.page });
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    loadUsers({ page: newPage });
  };

  // Handle row click
  const handleRowClick = (userId: string) => {
    setSelectedUserId(userId);
    loadUserDetail(userId);
    onOpen();
  };

  // Format relative time
  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'N/A';
    }
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Flex justify="flex-end" align="center">
          <Button
            leftIcon={<RefreshCw size={16} />}
            onClick={handleRefresh}
            size="sm"
            variant="outline"
          >
            Refresh
          </Button>
        </Flex>

        {/* Stats Cards */}
        {statsLoading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="blue.500" />
          </Flex>
        ) : stats && (
          <>
            {/* DAU/WAU/MAU */}
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <HStack spacing={3} mb={3}>
                    <Box p={2} bg="green.100" borderRadius="md">
                      <Users size={20} color="#38A169" />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Daily Active Users
                    </Text>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="green.500">
                    {stats.dau}
                  </Text>
                  <Text fontSize="sm" color="gray.500">Today</Text>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <HStack spacing={3} mb={3}>
                    <Box p={2} bg="blue.100" borderRadius="md">
                      <Activity size={20} color="#3182CE" />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Weekly Active Users
                    </Text>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="blue.500">
                    {stats.wau}
                  </Text>
                  <Text fontSize="sm" color="gray.500">Last 7 days</Text>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <HStack spacing={3} mb={3}>
                    <Box p={2} bg="purple.100" borderRadius="md">
                      <TrendingUp size={20} color="#805AD5" />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      Monthly Active Users
                    </Text>
                  </HStack>
                  <Text fontSize="3xl" fontWeight="bold" color="purple.500">
                    {stats.mau}
                  </Text>
                  <Text fontSize="sm" color="gray.500">Last 30 days</Text>
                </Box>
              </GridItem>
            </Grid>

            {/* Request Stats */}
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>Requests Today</StatLabel>
                    <StatNumber>{stats.total_requests_today.toLocaleString()}</StatNumber>
                    <StatHelpText>API calls</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>Requests This Week</StatLabel>
                    <StatNumber>{stats.total_requests_week.toLocaleString()}</StatNumber>
                    <StatHelpText>Last 7 days</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>

              <GridItem>
                <Box bg="white" p={6} borderRadius="md" shadow="sm">
                  <Stat>
                    <StatLabel>Requests This Month</StatLabel>
                    <StatNumber>{stats.total_requests_month.toLocaleString()}</StatNumber>
                    <StatHelpText>Last 30 days</StatHelpText>
                  </Stat>
                </Box>
              </GridItem>
            </Grid>
          </>
        )}

        {/* Filters */}
        <Box bg="white" p={6} borderRadius="md" shadow="sm">
          <HStack spacing={4}>
            <InputGroup flex={2}>
              <InputLeftElement pointerEvents="none">
                <Search size={16} color="gray" />
              </InputLeftElement>
              <Input
                placeholder="Search by username or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </InputGroup>

            <Select
              value={`${sortBy}-${sortOrder}`}
              onChange={handleSortChange}
              flex={1}
              maxW="250px"
            >
              <option value="last_activity-desc">Last Activity (Recent)</option>
              <option value="last_activity-asc">Last Activity (Oldest)</option>
              <option value="total_requests-desc">Total Requests (High)</option>
              <option value="total_requests-asc">Total Requests (Low)</option>
              <option value="username-asc">Username A-Z</option>
              <option value="username-desc">Username Z-A</option>
            </Select>

            <Button colorScheme="blue" onClick={handleSearch}>
              Search
            </Button>
          </HStack>
        </Box>

        {/* Users Table */}
        <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
          {loading ? (
            <Flex justify="center" align="center" py={20}>
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : users.length === 0 ? (
            <Flex justify="center" align="center" py={20}>
              <Text fontSize="lg" color="gray.500">
                No users found
              </Text>
            </Flex>
          ) : (
            <>
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th>User</Th>
                    <Th>Email</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Total Requests</Th>
                    <Th isNumeric>Today</Th>
                    <Th>Last Activity</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {users.map((user) => (
                    <Tr
                      key={user.id}
                      _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                      onClick={() => handleRowClick(user.id)}
                    >
                      <Td>
                        <HStack spacing={3}>
                          <Avatar size="sm" name={user.username} src={user.avatar || undefined} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">{user.username}</Text>
                            <Badge size="sm" colorScheme={user.role === 'admin' ? 'purple' : 'gray'}>
                              {user.role}
                            </Badge>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">{user.email}</Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={user.is_blocked ? 'red' : 'green'}>
                          {user.is_blocked ? 'Blocked' : 'Active'}
                        </Badge>
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="medium">{user.total_requests.toLocaleString()}</Text>
                      </Td>
                      <Td isNumeric>
                        <Text fontWeight="medium" color={user.requests_today > 0 ? 'green.500' : 'gray.400'}>
                          {user.requests_today}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600">
                          {formatRelativeTime(user.last_activity)}
                        </Text>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>

              {/* Pagination */}
              {pagination.pages > 1 && (
                <Flex justify="space-between" align="center" p={4} borderTop="1px" borderColor="gray.200">
                  <Text fontSize="sm" color="gray.600">
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
                  </Text>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      isDisabled={pagination.page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      isDisabled={pagination.page === pagination.pages}
                    >
                      Next
                    </Button>
                  </HStack>
                </Flex>
              )}
            </>
          )}
        </Box>

        {/* User Detail Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>User Activity Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              {detailLoading ? (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              ) : userDetail ? (
                <VStack spacing={4} align="stretch">
                  {/* User Info */}
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text fontWeight="bold" fontSize="lg">{userDetail.username}</Text>
                    <Text color="gray.600">{userDetail.email}</Text>
                  </Box>

                  {/* Summary Stats */}
                  <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                    <Box p={4} bg="blue.50" borderRadius="md" textAlign="center">
                      <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                        {userDetail.total_requests.toLocaleString()}
                      </Text>
                      <Text fontSize="sm" color="gray.600">Total Requests (30d)</Text>
                    </Box>
                    <Box p={4} bg="green.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">First Activity</Text>
                      <Text fontSize="sm" color="green.600">
                        {userDetail.first_activity
                          ? format(new Date(userDetail.first_activity), 'MMM dd, yyyy')
                          : 'N/A'}
                      </Text>
                    </Box>
                    <Box p={4} bg="purple.50" borderRadius="md" textAlign="center">
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Last Activity</Text>
                      <Text fontSize="sm" color="purple.600">
                        {formatRelativeTime(userDetail.last_activity)}
                      </Text>
                    </Box>
                  </Grid>

                  {/* Daily Stats Table */}
                  <Box>
                    <Text fontWeight="medium" mb={2}>Daily Activity (Last 30 Days)</Text>
                    <Box maxH="300px" overflowY="auto" border="1px" borderColor="gray.200" borderRadius="md">
                      <Table size="sm">
                        <Thead bg="gray.50" position="sticky" top={0}>
                          <Tr>
                            <Th>Date</Th>
                            <Th isNumeric>Requests</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {userDetail.daily_stats.map((stat) => (
                            <Tr key={stat.date}>
                              <Td>{format(new Date(stat.date), 'MMM dd, yyyy')}</Td>
                              <Td isNumeric fontWeight="medium">{stat.request_count}</Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  </Box>
                </VStack>
              ) : (
                <Text color="gray.500">No data available</Text>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </Box>
  );
};

export default ActivityPage;
